import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import { toast } from 'react-toastify';
import '../../components/admin/AdminLayout.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filter, setFilter] = useState('all');
  const [showPromoterModal, setShowPromoterModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [promoterForm, setPromoterForm] = useState({
    promoterType: 'friend',
    expiresInMonths: '',
    notes: ''
  });

  const fetchUsers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      let url = `/admin/users?page=${page}&limit=20`;
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      if (filter === 'premium') {
        url += '&subscription=premium';
      } else if (filter === 'free') {
        url += '&subscription=free';
      } else if (filter === 'promoter') {
        url += '&promoter=true';
      } else if (filter === 'suspended') {
        url += '&suspended=true';
      }
      
      const response = await api.get(url);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [search, filter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchUsers(1);
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchUsers]);

  const handleSuspend = async (userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    
    try {
      await api.post(`/admin/users/${userId}/suspend`, { reason: 'Admin action' });
      toast.success('User suspended');
      fetchUsers(pagination.page);
    } catch (error) {
      toast.error('Failed to suspend user');
    }
  };

  const handleUnsuspend = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/unsuspend`);
      toast.success('User unsuspended');
      fetchUsers(pagination.page);
    } catch (error) {
      toast.error('Failed to unsuspend user');
    }
  };

  const handleUpgrade = async (userId) => {
    const months = window.prompt('How many months of premium?', '1');
    if (!months) return;
    
    try {
      await api.post(`/admin/users/${userId}/upgrade`, { months: parseInt(months) });
      toast.success('User upgraded to premium');
      fetchUsers(pagination.page);
    } catch (error) {
      toast.error('Failed to upgrade user');
    }
  };

  const handleDowngrade = async (userId) => {
    if (!window.confirm('Are you sure you want to downgrade this user to free?')) return;
    
    try {
      await api.post(`/admin/users/${userId}/downgrade`);
      toast.success('User downgraded to free');
      fetchUsers(pagination.page);
    } catch (error) {
      toast.error('Failed to downgrade user');
    }
  };

  const openPromoterModal = (user) => {
    setSelectedUser(user);
    setPromoterForm({
      promoterType: 'friend',
      expiresInMonths: '',
      notes: ''
    });
    setShowPromoterModal(true);
  };

  const handleGrantPromoter = async () => {
    try {
      await api.post(`/admin/promoters/${selectedUser._id}`, {
        promoterType: promoterForm.promoterType,
        expiresInMonths: promoterForm.expiresInMonths ? parseInt(promoterForm.expiresInMonths) : null,
        notes: promoterForm.notes
      });
      toast.success('Promoter status granted!');
      setShowPromoterModal(false);
      fetchUsers(pagination.page);
    } catch (error) {
      toast.error('Failed to grant promoter status');
    }
  };

  const handleRevokePromoter = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke promoter status?')) return;
    
    try {
      await api.delete(`/admin/promoters/${userId}`);
      toast.success('Promoter status revoked');
      fetchUsers(pagination.page);
    } catch (error) {
      toast.error('Failed to revoke promoter status');
    }
  };

  const getUserBadges = (user) => {
    const badges = [];
    
    if (user.role === 'super_admin') {
      badges.push(<span key="super" className="admin-badge admin">👑 Super Admin</span>);
    } else if (user.role === 'admin') {
      badges.push(<span key="admin" className="admin-badge admin">🛡️ Admin</span>);
    }
    
    if (user.isPromoter) {
      badges.push(<span key="promoter" className="admin-badge promoter">🎁 Promoter</span>);
    } else if (user.subscription?.plan === 'premium' && user.subscription?.status === 'active') {
      badges.push(<span key="premium" className="admin-badge premium">⭐ Premium</span>);
    } else {
      badges.push(<span key="free" className="admin-badge free">Free</span>);
    }
    
    if (user.isSuspended) {
      badges.push(<span key="suspended" className="admin-badge suspended">🚫 Suspended</span>);
    }
    
    return badges;
  };

  return (
    <AdminLayout>
      <div className="admin-table-container">
        <div className="admin-table-header">
          <div className="admin-table-title">All Users ({pagination.total})</div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select 
              className="admin-form-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="all">All Users</option>
              <option value="premium">Premium</option>
              <option value="free">Free</option>
              <option value="promoter">Promoters</option>
              <option value="suspended">Suspended</option>
            </select>
            <input
              type="text"
              placeholder="Search by email or name..."
              className="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="admin-loading">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty-icon">👥</div>
            <p>No users found</p>
          </div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div>
                        <strong>{user.firstName} {user.lastName}</strong>
                        <br />
                        <small style={{ color: '#6b7280' }}>{user.email}</small>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {getUserBadges(user)}
                      </div>
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="admin-actions">
                        <Link 
                          to={`/admin/users/${user._id}`}
                          className="admin-btn admin-btn-secondary admin-btn-small"
                        >
                          View
                        </Link>
                        
                        {!user.isPromoter && user.subscription?.plan !== 'premium' && (
                          <>
                            <button
                              onClick={() => handleUpgrade(user._id)}
                              className="admin-btn admin-btn-success admin-btn-small"
                            >
                              Upgrade
                            </button>
                            <button
                              onClick={() => openPromoterModal(user)}
                              className="admin-btn admin-btn-primary admin-btn-small"
                            >
                              Make Promoter
                            </button>
                          </>
                        )}
                        
                        {(user.isPromoter || user.subscription?.plan === 'premium') && (
                          <>
                            {user.isPromoter ? (
                              <button
                                onClick={() => handleRevokePromoter(user._id)}
                                className="admin-btn admin-btn-danger admin-btn-small"
                              >
                                Revoke
                              </button>
                            ) : (
                              <button
                                onClick={() => handleDowngrade(user._id)}
                                className="admin-btn admin-btn-danger admin-btn-small"
                              >
                                Downgrade
                              </button>
                            )}
                          </>
                        )}
                        
                        {!user.isSuspended && user.role === 'user' && (
                          <button
                            onClick={() => handleSuspend(user._id)}
                            className="admin-btn admin-btn-danger admin-btn-small"
                          >
                            Suspend
                          </button>
                        )}
                        
                        {user.isSuspended && (
                          <button
                            onClick={() => handleUnsuspend(user._id)}
                            className="admin-btn admin-btn-success admin-btn-small"
                          >
                            Unsuspend
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="admin-pagination">
              <button
                className="admin-pagination-btn"
                disabled={pagination.page === 1}
                onClick={() => fetchUsers(pagination.page - 1)}
              >
                Previous
              </button>
              <span className="admin-pagination-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                className="admin-pagination-btn"
                disabled={pagination.page === pagination.pages}
                onClick={() => fetchUsers(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Promoter Modal */}
      {showPromoterModal && (
        <div className="admin-modal-overlay" onClick={() => setShowPromoterModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">Grant Promoter Status</h3>
              <button className="admin-modal-close" onClick={() => setShowPromoterModal(false)}>×</button>
            </div>
            
            <p>Granting free premium access to: <strong>{selectedUser?.email}</strong></p>
            
            <div className="admin-form-group">
              <label className="admin-form-label">Promoter Type</label>
              <select 
                className="admin-form-select"
                value={promoterForm.promoterType}
                onChange={(e) => setPromoterForm({ ...promoterForm, promoterType: e.target.value })}
              >
                <option value="friend">Friend / Family</option>
                <option value="influencer">Influencer</option>
                <option value="beta_tester">Beta Tester</option>
                <option value="contest">Contest Winner</option>
                <option value="support">Support Compensation</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="admin-form-group">
              <label className="admin-form-label">Duration (leave empty for lifetime)</label>
              <input
                type="number"
                className="admin-form-input"
                placeholder="Months (empty = lifetime)"
                value={promoterForm.expiresInMonths}
                onChange={(e) => setPromoterForm({ ...promoterForm, expiresInMonths: e.target.value })}
              />
            </div>
            
            <div className="admin-form-group">
              <label className="admin-form-label">Notes (internal)</label>
              <textarea
                className="admin-form-textarea"
                placeholder="e.g., College friend helping promote on TikTok"
                value={promoterForm.notes}
                onChange={(e) => setPromoterForm({ ...promoterForm, notes: e.target.value })}
              />
            </div>
            
            <div className="admin-modal-actions">
              <button 
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowPromoterModal(false)}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-primary"
                onClick={handleGrantPromoter}
              >
                Grant Promoter Status
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
